import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubjectLanguageVariants } from '@features/user';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Box, Link, Typography } from '@mui/material';

import type { PatientClinicalInfo, QuestionnaireInfo } from '../../../../types';
import { PatientOverviewSection } from '../../AppointmentCard.styles';
import {
  countPendingDiagnosisSuggestions,
  countPendingMedicationSuggestions,
  formatDiagnoses,
  formatMedications,
  formatQuestionnaireDate,
  formatQuestionnairesDetail,
} from '../../AppointmentCard.utils';
import { PatientInfoItem } from '../PatientInfoItem';

import { SuggestionPopover } from './SuggestionPopover';

interface PatientOverviewProps {
  clinicalInfo?: PatientClinicalInfo | null;
  patientId?: string;
}

/**
 * Patient overview section displaying clinical information
 * Shows pronouns, diagnoses, medications, questionnaires, etc.
 */
export const PatientOverview: React.FC<PatientOverviewProps> = ({ clinicalInfo, patientId }) => {
  const navigate = useNavigate();
  const { singularCap: subjectLanguage } = useSubjectLanguageVariants();
  const [showAllDiagnoses, setShowAllDiagnoses] = useState(false);
  const [showAllQuestionnaires, setShowAllQuestionnaires] = useState(false);

  // Format the data
  const pronouns = clinicalInfo?.pronouns || 'Not specified';
  const diagnoses = formatDiagnoses(clinicalInfo?.diagnoses);
  const medications = formatMedications(clinicalInfo?.medications);
  const sortedQuestionnaires: QuestionnaireInfo[] = formatQuestionnairesDetail(clinicalInfo?.questionnaires);

  // Get accepted and active diagnoses for list rendering
  const acceptedDiagnoses = clinicalInfo?.diagnoses?.filter(d => d.status === 'accepted' && d.name && d.is_active !== false) || [];

  // Count pending suggestions
  const pendingDiagnosisCount = countPendingDiagnosisSuggestions(clinicalInfo?.diagnoses);
  const pendingMedicationCount = countPendingMedicationSuggestions(clinicalInfo?.medications);

  return (
    <PatientOverviewSection>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {subjectLanguage} Overview
      </Typography>

      <PatientInfoItem label="Pronouns" value={pronouns} />

      <PatientInfoItem
        label="Diagnosis"
        value={
          acceptedDiagnoses.length > 1 ? (
            <>
              {pendingDiagnosisCount > 0 && (
                <SuggestionPopover
                  suggestions={clinicalInfo?.diagnoses || []}
                  type="diagnosis"
                  patientId={patientId}
                  count={pendingDiagnosisCount}
                />
              )}
              <Box
                component="ul"
                sx={{ margin: 0, paddingLeft: 2.5, listStyleType: 'disc' }}
                onMouseEnter={() => acceptedDiagnoses.length > 4 && setShowAllDiagnoses(true)}
                onMouseLeave={() => setShowAllDiagnoses(false)}
              >
                {(showAllDiagnoses ? acceptedDiagnoses : acceptedDiagnoses.slice(0, acceptedDiagnoses.length <= 4 ? 4 : 3)).map((diagnosis, index) => (
                  <Typography component="li" variant="body2" key={diagnosis.diagnosis_id || index}>
                    {diagnosis.name}
                  </Typography>
                ))}
                {acceptedDiagnoses.length > 4 && !showAllDiagnoses && (
                  <Typography component="li" variant="body2" sx={{ listStyleType: 'none', ml: -1.5 }}>
                    <Box
                      component="span"
                      sx={{
                        fontStyle: 'italic',
                        color: 'text.secondary',
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        }
                      }}
                    >
                      ...and {acceptedDiagnoses.length - 3} more
                    </Box>
                  </Typography>
                )}
              </Box>
            </>
          ) : (
            <>
              {diagnoses}
              {diagnoses === 'None.' && patientId && (
                <>
                  {' '}
                  <Link
                    component="button"
                    variant="body2"
                    underline="hover"
                    sx={{ cursor: 'pointer' }}
                    onClick={e => {
                      e.stopPropagation();
                      navigate(`/patient/${patientId}`);
                    }}
                  >
                    Add Diagnosis?
                  </Link>
                </>
              )}
              {pendingDiagnosisCount > 0 && (
                <SuggestionPopover
                  suggestions={clinicalInfo?.diagnoses || []}
                  type="diagnosis"
                  patientId={patientId}
                  count={pendingDiagnosisCount}
                />
              )}
            </>
          )
        }
      />

      <PatientInfoItem
        label="Questionnaires"
        value={
          sortedQuestionnaires.length === 0 ? (
            <>
              None.{' '}
              {patientId && (
                <Link
                  component="button"
                  variant="body2"
                  underline="hover"
                  sx={{ cursor: 'pointer' }}
                  onClick={e => {
                    e.stopPropagation();
                    navigate(`/patient/${patientId}`);
                  }}
                >
                  Send Questionnaire?
                </Link>
              )}
            </>
          ) : (
            <Box
              component="ul"
              sx={{ margin: 0, paddingLeft: 2.5, listStyleType: 'disc' }}
              onMouseEnter={() => sortedQuestionnaires.length > 3 && setShowAllQuestionnaires(true)}
              onMouseLeave={() => setShowAllQuestionnaires(false)}
            >
              {(showAllQuestionnaires ? sortedQuestionnaires : sortedQuestionnaires.slice(0, sortedQuestionnaires.length <= 3 ? 3 : 2)).map((questionnaire, index) => (
                <Typography component="li" variant="body2" key={index}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                    <Box component="span">
                      {questionnaire.name || 'Unknown'}:
                    </Box>
                    {questionnaire.score != null && (
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        {questionnaire.score}
                      </Box>
                    )}
                    {questionnaire.trend && (
                      <>
                        {questionnaire.trend === 'up' && (
                          <TrendingUpIcon sx={{ fontSize: 16, color: 'error.main' }} />
                        )}
                        {questionnaire.trend === 'down' && (
                          <TrendingDownIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        )}
                        {questionnaire.trend === 'stable' && (
                          <TrendingFlatIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        )}
                      </>
                    )}
                    {questionnaire.date_completed && (
                      <Box component="span" sx={{ color: 'text.secondary' }}>
                        {formatQuestionnaireDate(questionnaire.date_completed)}
                      </Box>
                    )}
                  </Box>
                </Typography>
              ))}
              {sortedQuestionnaires.length > 3 && !showAllQuestionnaires && (
                <Typography component="li" variant="body2" sx={{ listStyleType: 'none', ml: -1.5 }}>
                  <Box
                    component="span"
                    sx={{
                      fontStyle: 'italic',
                      color: 'text.secondary',
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline',
                      }
                    }}
                  >
                    ...and {sortedQuestionnaires.length - 2} more
                  </Box>
                </Typography>
              )}
            </Box>
          )
        }
      />

      <PatientInfoItem
        label="Medications"
        value={
          <>
            {medications}
            {medications === 'None.' && patientId && (
              <>
                {' '}
                <Link
                  component="button"
                  variant="body2"
                  underline="hover"
                  sx={{ cursor: 'pointer' }}
                  onClick={e => {
                    e.stopPropagation();
                    navigate(`/patient/${patientId}`);
                  }}
                >
                  Add Medication?
                </Link>
              </>
            )}
            {pendingMedicationCount > 0 && (
              <SuggestionPopover
                suggestions={clinicalInfo?.medications || []}
                type="medication"
                patientId={patientId}
                count={pendingMedicationCount}
              />
            )}
          </>
        }
      />
    </PatientOverviewSection>
  );
};